import { createAsyncThunk } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/redux/store'
import {
  addAIPlayerTestChatMessage,
  setAIPlayerTestChatConnectionStatus,
  setAIPlayerTestChatError,
  setAIPlayerTestChatGenerating,
} from '@/redux/slices/aiPlayerTestChatSlice'
import WebSocketManager from '@/services/websocketManager'

const wsManager = WebSocketManager.getInstance()

export const connectAIPlayerTestChatWebSocket = createAsyncThunk<
  void,
  { jwtToken: string; botId: string; roomId: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/connectAIPlayerTestChat',
  async ({ jwtToken, botId, roomId }, { rejectWithValue }) => {
    try {
      return await wsManager.connectToAIPlayerTestChat(jwtToken, botId, roomId)
    } catch (error) {
      return rejectWithValue(
        (error as Error).message ||
          'Failed to connect to AI player test chat websocket'
      )
    }
  }
)

export const sendAIPlayerTestChatMessage = createAsyncThunk<
  void,
  { message: string; user?: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/sendAIPlayerTestChatMessage',
  async ({ message, user }, { rejectWithValue, dispatch }) => {
    dispatch(setAIPlayerTestChatGenerating(true))
    dispatch(
      addAIPlayerTestChatMessage({
        id: Date.now(),
        message,
        user: user || '',
        streaming: false,
      })
    )
    const success = wsManager.sendMessage('aiPlayerTestChat', {
      type: 'test_message',
      message,
    })
    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const getPreviousAIPlayerChats = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('websocket/getPreviousAIPlayerChats', async (_, { rejectWithValue }) => {
  const success = wsManager.sendMessage('aiPlayerTestChat', {
    type: 'previous_chat',
  })
  if (!success) {
    return rejectWithValue('WebSocket is not connected')
  }
})

export const closeAIPlayerTestChat = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('websocket/closeAIPlayerTestChat', async (_, { rejectWithValue }) => {
  const success = wsManager.sendMessage('aiPlayerTestChat', {
    type: 'test_chat_closed',
  })
  if (!success) {
    return rejectWithValue('WebSocket is not connected')
  }
})

export const saveAIPlayerTestChatMessages = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('websocket/saveAIPlayerTestChatMessages', async (_, { rejectWithValue }) => {
  const success = wsManager.sendMessage('aiPlayerTestChat', {
    type: 'save_messages',
  })
  if (!success) {
    return rejectWithValue('WebSocket is not connected')
  }
})

export const disconnectAIPlayerTestChatWebSocket = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('websocket/disconnectAIPlayerTestChat', async (_, { dispatch }) => {
  try {
    await wsManager.disconnect('aiPlayerTestChat')
    dispatch(setAIPlayerTestChatConnectionStatus(false))
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Unknown error disconnecting AI player test chat WebSocket'
    dispatch(setAIPlayerTestChatError(errorMessage))
  }
})
